import { useRef, useState } from "react";
import type { FormEvent, RefObject } from "react";
import { ArrowRight, Check, LoaderCircle, Mail } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { fractionalInquirySchema } from "@shared/fractionalInquiry";
import { formatNationalPhone } from "@shared/profileInputs";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

type InquiryService =
  | "general"
  | "talent-acquisition"
  | "employee-experience"
  | "compensation";

type FractionalInquiryDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusRef?: RefObject<HTMLButtonElement | null>;
};

const freshForm = () => ({
  name: "",
  email: "",
  phone: "",
  organization: "",
  service: "general" as InquiryService,
  message: "",
  privacyAcknowledged: false,
  website: "",
});

function newSubmissionId() {
  return crypto.randomUUID();
}

export default function FractionalInquiryDrawer({
  open,
  onOpenChange,
  returnFocusRef,
}: FractionalInquiryDrawerProps) {
  const [form, setForm] = useState(freshForm);
  const [outcome, setOutcome] = useState<"idle" | "success" | "failed">("idle");
  const [validationMessage, setValidationMessage] = useState("");
  const submittingRef = useRef(false);
  const submissionIdRef = useRef<string | null>(null);
  const submit = trpc.fractionalServices.submitInquiry.useMutation({ retry: false });

  const setValue = <K extends keyof ReturnType<typeof freshForm>>(
    key: K,
    value: ReturnType<typeof freshForm>[K],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
    setValidationMessage("");
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submit.isPending || submittingRef.current || outcome === "failed" || !form.privacyAcknowledged) return;
    const candidate = {
        submissionId: submissionIdRef.current ??= newSubmissionId(),
        name: form.name,
        email: form.email,
        phone: form.phone,
        organization: form.organization,
        service: form.service,
        message: form.message,
        privacyAcknowledged: true,
        website: form.website,
      };
    const validated = fractionalInquirySchema.safeParse(candidate);
    if (!validated.success) {
      setValidationMessage("Please check your name, email, and message. If you include a phone number, enter all 10 digits.");
      return;
    }
    submittingRef.current = true;
    setOutcome("idle");
    try {
      const result = await submit.mutateAsync(validated.data);
      setOutcome(result?.delivered === true ? "success" : "failed");
    } catch {
      setOutcome("failed");
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="fractional-inquiry"
        aria-describedby="fractional-inquiry-description"
        onCloseAutoFocus={(event) => {
          if (returnFocusRef?.current) {
            event.preventDefault();
            returnFocusRef.current.focus();
          }
        }}
      >
        <SheetHeader className="fractional-inquiry__header">
          <span className="fractional-inquiry__eyebrow">THE CHI ALLIANCE / PEOPLE FIRST</span>
          <SheetTitle className="fractional-inquiry__title">Let’s talk about what’s next.</SheetTitle>
          <SheetDescription
            id="fractional-inquiry-description"
            className="fractional-inquiry__description"
          >
            Tell us a little about your organization. A CHI leader will follow up personally.
          </SheetDescription>
        </SheetHeader>

        {outcome === "success" ? (
          <div className="fractional-inquiry__success" role="status" aria-live="polite">
            <span className="fractional-inquiry__success-mark"><Check size={22} aria-hidden="true" /></span>
            <p className="fractional-inquiry__eyebrow">MESSAGE DELIVERED</p>
            <h3>Thank you, {form.name.split(" ")[0] || "friend"}.</h3>
            <p>Your note is with our team. We’ll be in touch soon.</p>
            <button type="button" className="fractional-inquiry__secondary" onClick={() => onOpenChange(false)}>
              Close
            </button>
          </div>
        ) : (
          <form className="fractional-inquiry__form" onSubmit={onSubmit}>
            <label className="fractional-field">
              <span>Your name <i aria-hidden="true">*</i></span>
              <input
                name="name"
                autoComplete="name"
                required
                minLength={2}
                maxLength={120}
                value={form.name}
                onChange={(event) => setValue("name", event.target.value)}
              />
            </label>
            <label className="fractional-field">
              <span>Work email <i aria-hidden="true">*</i></span>
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                maxLength={254}
                value={form.email}
                onChange={(event) => setValue("email", event.target.value)}
              />
            </label>
            <label className="fractional-field">
              <span>Phone <small>Optional</small></span>
              <input
                name="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(###) ###-####"
                pattern="\(\d{3}\) \d{3}-\d{4}"
                value={form.phone}
                onChange={(event) => setValue("phone", formatNationalPhone("+1", event.target.value))}
              />
            </label>
            <label className="fractional-field">
              <span>Organization <small>Optional</small></span>
              <input
                name="organization"
                autoComplete="organization"
                maxLength={160}
                value={form.organization}
                onChange={(event) => setValue("organization", event.target.value)}
              />
            </label>
            <label className="fractional-field">
              <span>Area of interest <i aria-hidden="true">*</i></span>
              <select
                name="service"
                required
                value={form.service}
                onChange={(event) => setValue("service", event.target.value as InquiryService)}
              >
                <option value="general">I’m not sure yet</option>
                <option value="talent-acquisition">Talent acquisition &amp; retained search</option>
                <option value="employee-experience">Employee experience &amp; organizational effectiveness</option>
                <option value="compensation">Compensation strategy &amp; job architecture</option>
              </select>
            </label>
            <label className="fractional-field">
              <span>What would you like to work through? <i aria-hidden="true">*</i></span>
              <textarea
                name="message"
                required
                minLength={10}
                maxLength={3000}
                rows={4}
                value={form.message}
                onChange={(event) => setValue("message", event.target.value)}
              />
              <small className="fractional-field__count">{form.message.length}/3000</small>
            </label>

            <div className="fractional-inquiry__trap" aria-hidden="true">
              <label htmlFor="fractional-website">Website</label>
              <input
                id="fractional-website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(event) => setValue("website", event.target.value)}
              />
            </div>

            <label className="fractional-consent">
              <input
                type="checkbox"
                name="privacyAcknowledged"
                required
                checked={form.privacyAcknowledged}
                onChange={(event) => setValue("privacyAcknowledged", event.target.checked)}
              />
              <span>
                I understand my information will be used only to respond to this inquiry.{" "}
                <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy policy</a>
              </span>
            </label>

            {validationMessage && <p role="alert" className="fractional-inquiry__error">{validationMessage}</p>}
            {outcome === "failed" && (
              <div className="fractional-inquiry__error" role="alert">
                <strong>Your inquiry wasn’t confirmed.</strong>
                <span>Please don’t submit again just yet. You can contact us directly at{" "}
                  <a href="mailto:info@thechialliance.com">info@thechialliance.com</a>.
                </span>
              </div>
            )}

            <button
              className="fractional-inquiry__submit"
              type="submit"
              disabled={submit.isPending || outcome === "failed"}
            >
              {submit.isPending ? (
                <><LoaderCircle size={17} className="fractional-inquiry__loader" aria-hidden="true" /> Sending inquiry</>
              ) : (
                <>Send inquiry <ArrowRight size={17} aria-hidden="true" /></>
              )}
            </button>
            {submit.isPending && <p className="fractional-inquiry__pending" role="status">Your message is being delivered. Please keep this window open.</p>}
            <p className="fractional-inquiry__privacy-note">
              <Mail size={14} aria-hidden="true" /> No mailing list. Just a thoughtful reply.
            </p>
            <p className="fractional-inquiry__privacy-note">
              Please don’t include confidential employee records, medical information, or payment details.
            </p>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
